import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { invoiceQueryKeys } from "../api/queryKeys";

import { instance } from "@/lib/axios/instance";
import {
  handleProblemDetailsError,
  ProblemDetails,
} from "@/lib/helpers/handleProblemDetailsError";
import useToast from "@/lib/hooks/useToast";
import { CampaignIntegrationMonthType } from "@/types/response/CampaignIntegrationMonthType";
import { PagedResponse } from "@/types/response/PagedResponse";

export type BulkUpdateCampaignIntegrationInvoiceItem = {
  campaignId: string;
  clientIntegrationModuleId: string;
  clientId2: string;
  amount: number;
};

export type BulkUpdateCampaignIntegrationInvoicesRequest = {
  invoices: BulkUpdateCampaignIntegrationInvoiceItem[];
};

type UseBulkUpdateCampaignIntegrationInvoicesParams = {
  tenantIntegrationModuleId: string;
  year: string;
  month: string;
  tenantId?: string | null;
};

/**
 * Updates campaign integration invoices in bulk for a tenant integration module and month.
 * Calls PUT /api/v2/Integrations/{tenantIntegrationModuleId}/Invoices/{year}/{month}/Bulk.
 */
export function useBulkUpdateCampaignIntegrationInvoices(
  params: UseBulkUpdateCampaignIntegrationInvoicesParams
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    AxiosResponse,
    AxiosError<ProblemDetails>,
    BulkUpdateCampaignIntegrationInvoicesRequest,
    { previousQueries: Array<{ queryKey: readonly unknown[]; data: unknown }> }
  >({
    mutationFn: (request: BulkUpdateCampaignIntegrationInvoicesRequest) =>
      instance
        .put<AxiosResponse>(
          `/api/v2/Integrations/${params.tenantIntegrationModuleId}/Invoices/${params.year}/${params.month}/Bulk`,
          { invoices: request.invoices },
          {
            params: {
              tenantId: params.tenantId || undefined,
            },
          }
        )
        .then((res) => res.data),
    onMutate: async (request) => {
      await queryClient.cancelQueries({
        queryKey: invoiceQueryKeys.cache.integrationModuleCampaigns.byIntegrationModuleYearMonth(
          params.tenantIntegrationModuleId,
          params.year,
          params.month
        ),
        exact: false,
      });

      const previousQueries: Array<{
        queryKey: readonly unknown[];
        data: unknown;
      }> = [];

      const affectedQueries = queryClient
        .getQueryCache()
        .findAll({
          queryKey: invoiceQueryKeys.cache.integrationModuleCampaigns.byIntegrationModuleYearMonth(
            params.tenantIntegrationModuleId,
            params.year,
            params.month
          ),
          exact: false,
        });

      affectedQueries.forEach((query) => {
        if (query.state.data) {
          previousQueries.push({
            queryKey: query.queryKey,
            data: query.state.data,
          });
        }
      });

      queryClient.setQueriesData(
        {
          queryKey: invoiceQueryKeys.cache.integrationModuleCampaigns.byIntegrationModuleYearMonth(
            params.tenantIntegrationModuleId,
            params.year,
            params.month
          ),
          exact: false,
        },
        (old: PagedResponse<CampaignIntegrationMonthType> | undefined) => {
          if (!old?.data) return old;

          const updatedData = old.data.map((item: CampaignIntegrationMonthType) => {
            const update = request.invoices.find(
              (inv) =>
                inv.campaignId === item.campaignId &&
                inv.clientId2 === item.clientId2 &&
                inv.clientIntegrationModuleId === item.clientIntegrationModuleId
            );
            if (update) {
              return { ...item, spent: update.amount };
            }
            return item;
          });

          return { ...old, data: updatedData };
        }
      );

      return { previousQueries };
    },
    onError: (error, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      handleProblemDetailsError({
        error,
        t,
        showToast,
        fallbackMessage: t("errors.somethingWentWrong"),
      });
    },
    onSuccess: () => {
      // Mark the local query as stale WITHOUT triggering a refetch.
      // The optimistic update from onMutate is already showing the correct value.
      // An immediate refetch would overwrite it before the async event consumer updates the read model.
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.integrationModuleCampaigns.byIntegrationModuleYearMonth(
          params.tenantIntegrationModuleId,
          params.year,
          params.month
        ),
        refetchType: "none",
      });
      // Cross-page queries: mark stale, refetchOnMount: true handles refresh on navigation
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ["campaignJobBoards"] });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.campaignJobBoardMonths.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clientBudgetMonth.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clients.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.transactions.all() });

      showToast(t("success.save", { entity: t("common.invoices") }));
    },
  });
}
